import { useEffect, useState } from "react";
import { CatalogRow } from "../CatalogRow";
import { fetchPriceHistory, fetchProducts, type PriceHistoryEntry, type Product } from "../catalog";
import { signIn } from "../session";

type ProductsState = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; products: Product[] };

type History = PriceHistoryEntry[] | "loading" | "error";

/**
 * Catalog price edit + history (#260). Lists every product from
 * catalog-service (already-proxied `GET /api/products`); one row expands at
 * a time into a price + reorder-point editor with a lazy-loaded price
 * history (fetched only when that row first opens, not upfront for all
 * ~35 products).
 */
export function CatalogScreen() {
  const [state, setState] = useState<ProductsState>({ status: "loading" });
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const [historyBySku, setHistoryBySku] = useState<Record<string, History>>({});

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setState({ status: "loading" });
    const result = await fetchProducts();
    if (result.kind === "ok") {
      setState({ status: "ready", products: result.value });
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setState({ status: "error", message: result.message });
    }
  }

  async function loadHistory(sku: string) {
    setHistoryBySku((h) => ({ ...h, [sku]: "loading" }));
    const result = await fetchPriceHistory(sku);
    if (result.kind === "ok") {
      setHistoryBySku((h) => ({ ...h, [sku]: result.value }));
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setHistoryBySku((h) => ({ ...h, [sku]: "error" }));
    }
  }

  function toggle(sku: string) {
    const next = expandedSku === sku ? null : sku;
    setExpandedSku(next);
    if (next && !historyBySku[next]) {
      void loadHistory(next);
    }
  }

  function replaceProduct(updated: Product) {
    setState((s) =>
      s.status === "ready"
        ? { ...s, products: s.products.map((p) => (p.sku === updated.sku ? updated : p)) }
        : s,
    );
  }

  if (state.status === "loading") {
    return <p className="status-message">Loading products…</p>;
  }

  if (state.status === "error") {
    return (
      <section className="screen-placeholder">
        <p className="status-error">Couldn't load the catalog ({state.message}).</p>
        <button type="button" onClick={() => void load()}>
          Retry
        </button>
      </section>
    );
  }

  return (
    <div className="catalog-table-wrap">
      <table className="catalog-table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Name</th>
            <th>Category</th>
            <th className="catalog-num">Unit price</th>
            <th className="catalog-num">Reorder point</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {state.products.map((product) => (
            <CatalogRow
              key={product.sku}
              product={product}
              expanded={expandedSku === product.sku}
              history={historyBySku[product.sku]}
              onToggle={() => toggle(product.sku)}
              onUpdated={replaceProduct}
              onPriceSaved={loadHistory}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
